import { User, Family } from 'shared/types';
import type { StoragePort } from 'shared/ports/storage_port';
import { storageAdapter } from '../platform/web/storage_adapter';

const STORAGE_KEYS = {
  AUTH_TOKEN: 'family_task_manager_token',
  GUARDIAN_SESSION: 'family_task_manager_guardian_session',
  DEVICE_TOKEN: 'family_task_manager_device_token',
  USER_DATA: 'family_task_manager_user',
  FAMILY_DATA: 'family_task_manager_family',
  THEME: 'family_task_manager_theme',
  LANGUAGE: 'family_task_manager_language',
  USE_SYSTEM_LANGUAGE: 'family_task_manager_use_system_language'
};

let storage: StoragePort = storageAdapter;

export function setStoragePort(port: StoragePort) {
  storage = port;
}

export const storageService = {
  // Token management
  setAuthToken: (token: string): void => {
    storage.set(STORAGE_KEYS.AUTH_TOKEN, token);
  },

  getAuthToken: (): string | null => {
    return storage.get(STORAGE_KEYS.AUTH_TOKEN);
  },

  removeAuthToken: (): void => {
    storage.remove(STORAGE_KEYS.AUTH_TOKEN);
  },

  // Guardian Supabase session (saved when switching to member, restored when switching back)
  setGuardianSession: (session: { access_token: string; refresh_token: string; user?: { id: string } }): void => {
    storage.setJSON(STORAGE_KEYS.GUARDIAN_SESSION, session);
  },
  getGuardianSession: (): { access_token: string; refresh_token: string; user?: { id: string } } | null => {
    return storage.getJSON<{ access_token: string; refresh_token: string; user?: { id: string } }>(STORAGE_KEYS.GUARDIAN_SESSION);
  },
  removeGuardianSession: (): void => {
    storage.remove(STORAGE_KEYS.GUARDIAN_SESSION);
  },

  // Device token management
  setDeviceToken: (token: string): void => {
    storage.set(STORAGE_KEYS.DEVICE_TOKEN, token);
  },

  getDeviceToken: (): string | null => {
    return storage.get(STORAGE_KEYS.DEVICE_TOKEN);
  },

  removeDeviceToken: (): void => {
    storage.remove(STORAGE_KEYS.DEVICE_TOKEN);
  },

  // User data management
  setUserData: (user: User): void => {
    storage.setJSON(STORAGE_KEYS.USER_DATA, user);
  },

  getUserData: (): User | null => {
    return storage.getJSON<User>(STORAGE_KEYS.USER_DATA);
  },

  removeUserData: (): void => {
    storage.remove(STORAGE_KEYS.USER_DATA);
  },

  // Family data management
  setFamilyData: (family: Family): void => {
    storage.setJSON(STORAGE_KEYS.FAMILY_DATA, family);
  },

  getFamilyData: (): Family | null => {
    return storage.getJSON<Family>(STORAGE_KEYS.FAMILY_DATA);
  },

  removeFamilyData: (): void => {
    storage.remove(STORAGE_KEYS.FAMILY_DATA);
  },

  // Theme management
  setTheme: (theme: 'light' | 'dark'): void => {
    storage.set(STORAGE_KEYS.THEME, theme);
    document.documentElement.setAttribute('data-theme', theme);
  },

  getTheme: (): 'light' | 'dark' => {
    const theme = storage.get(STORAGE_KEYS.THEME) as 'light' | 'dark' | null;
    return theme || 'light';
  },

  // Language management
  setLanguage: (language: string): void => {
    storage.set(STORAGE_KEYS.LANGUAGE, language);
  },

  getLanguage: (): string => {
    return storage.get(STORAGE_KEYS.LANGUAGE) || 'en';
  },

  hasLanguage: (): boolean => {
    return storage.get(STORAGE_KEYS.LANGUAGE) !== null;
  },

  setUseSystemLanguage: (useSystem: boolean): void => {
    storage.setJSON(STORAGE_KEYS.USE_SYSTEM_LANGUAGE, useSystem);
  },

  getUseSystemLanguage: (): boolean => {
    const value = storage.getJSON<boolean>(STORAGE_KEYS.USE_SYSTEM_LANGUAGE);
    return !!value;
  },

  // Clear all app data
  clearAllData: (): void => {
    Object.values(STORAGE_KEYS).forEach(key => storage.remove(key));
  },

  // Check if user is authenticated
  isAuthenticated: (): boolean => {
    const token = storageService.getAuthToken();
    const user = storageService.getUserData();
    return !!(token && user);
  },

  // Get user points from storage
  getUserPoints: (): number => {
    const user = storageService.getUserData();
    return user?.points || 0;
  },

  // Update user points in storage
  updateUserPoints: (newPoints: number): void => {
    const user = storageService.getUserData();
    if (user) {
      user.points = newPoints;
      storageService.setUserData(user);
    }
  },

  // Get family total points from storage
  getFamilyTotalPoints: (): number => {
    const family = storageService.getFamilyData();
    if (!family?.members) return 0;
    return family.members.reduce((total, member) => total + member.points, 0);
  },

  // Check if user is admin
  isUserAdmin: (): boolean => {
    const user = storageService.getUserData();
    return user?.is_admin || false;
  },

  // Get user name from storage
  getUserName: (): string => {
    const user = storageService.getUserData();
    return user?.name || 'Unknown User';
  },

  // Get family name from storage
  getFamilyName: (): string => {
    const family = storageService.getFamilyData();
    return family?.name || 'Unknown Family';
  }
};

export default storageService;
